import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { useShop } from '../contexts/ShopContext'
import { useToast } from '../contexts/ToastContext'
import { supabase } from '../lib/supabase'
import { ArrowRightIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import OnboardingProductsAndSuppliers, { type SellMode } from './OnboardingProductsAndSuppliers'
import type { ProductLine } from '../hooks/useProductLines'
import { usePlans } from '../hooks/usePlans'
import { planFromQuery, publicPlans } from '../lib/plans'
import OnboardingPlanChoice from './OnboardingPlanChoice'
import SetupFeeCard from './SetupFeeCard'
import {
  DEFAULT_IMPORT,
  DEFAULT_PRICING,
  ROUNDING_OPTIONS,
  buildGoLiveConfig,
  clampPricing,
  cleanShopifyDomain,
  cleanWooUrl,
  computePrice,
  type ImportChoice,
  type PricingChoice,
  type RoundingMode,
} from '../lib/onboarding'

/**
 * Self-serve merchant onboarding: from an invite link to a live, syncing store with no developer.
 *
 * Order matters: the store is created in step 2, because suppliers, product lines and the
 * business details all hang off the merchant it creates. A store created here starts PAUSED
 * (deploy_merchant start_paused) and only syncs once "Go live" writes pricing and unpauses it,
 * so nothing is ever listed at fallback prices.
 *
 * Step 3 is a loop, one product line at a time: search a brand or model across every
 * supplier's catalogue, select it, connect to the suppliers that carry it, then "Connect another
 * product line" or "Done". A merchant does not know what a supplier stocks, so suppliers are
 * only offered for products they just picked.
 *
 * The partner's invite link can pre-fill: ?name=&email=&platform=shopify|woocommerce&markup=&plan=
 *
 * Plan (W9b, user decision 2026-10-24): the trial runs on the plan picked in step 1. A plan with
 * a setup fee is created as 'pending_setup' and Go live waits until the fee is paid (SetupFeeCard).
 */

type Platform = 'shopify' | 'woocommerce'

const STEPS = [
  { title: 'Your business', hint: 'Name and addresses' },
  { title: 'Connect your store', hint: 'Shopify or WooCommerce' },
  { title: 'Products & suppliers', hint: 'What you sell, and from whom' },
  { title: 'Pricing', hint: 'Markup and VAT' },
  { title: 'Import preferences', hint: 'How products arrive' },
  { title: 'Review and go live', hint: 'Start syncing' },
]
const DONE = STEPS.length + 1

const SHOPIFY_SCOPES = ['write_products', 'write_inventory', 'write_locations', 'write_orders', 'write_publications']

interface ProviderRow { id: string; name: string; provider_type: string | null }

// supabase.functions.invoke hides a non-2xx body behind error.context; surface its message.
async function invokeFn<T = any>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body })
  if (error) {
    let message = error.message
    try {
      const ctx: any = (error as any).context
      const payload = typeof ctx?.json === 'function' ? await ctx.json() : null
      if (payload?.error) message = payload.error + (payload.details ? ` (${payload.details})` : '')
    } catch { /* keep the generic message */ }
    throw new Error(message)
  }
  return data as T
}

const field = 'flex flex-col gap-1.5'
const label = 'text-xs text-white/50'
const primaryBtn = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold text-sm disabled:opacity-50 transition-colors'
const ghostBtn = 'px-4 py-2.5 rounded-full border border-white/20 text-white/60 hover:text-white text-sm transition-colors'

const ShopOnboarding: React.FC = () => {
  const { user, signInWithGoogle, loading: authLoading } = useAuth()
  const { setCurrentShopId, refreshShops, currentShopId } = useShop()
  const toast = useToast()

  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)

  // Step 1: plan + business
  const { plans, error: plansError } = usePlans()
  const [planId, setPlanId] = useState<string | null>(null)
  useEffect(() => {
    if (planId === null && plans.length > 0) setPlanId(planFromQuery(plans, params.get('plan')))
  }, [plans, planId, params])
  // Without readable plans (an outage) the store is still created, on the old default trial.
  const planChosen = planId !== null || Boolean(plansError) || (plans.length > 0 && publicPlans(plans, 'merchant').length === 0)
  const [awaitingSetup, setAwaitingSetup] = useState(false)

  const [shopName, setShopName] = useState(params.get('name') ?? '')
  const [contactEmail, setContactEmail] = useState(params.get('email') ?? user?.email ?? '')
  const [company, setCompany] = useState('')
  const [vatId, setVatId] = useState('')
  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [zip, setZip] = useState('')
  const [country, setCountry] = useState('')
  const [phone, setPhone] = useState('')

  // Step 2: store
  const [platform, setPlatform] = useState<Platform>(
    params.get('platform') === 'woocommerce' && !params.get('shopify_install') ? 'woocommerce' : 'shopify',
  )
  const [shopifyDomain, setShopifyDomain] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [wooUrl, setWooUrl] = useState('')
  const [wooKey, setWooKey] = useState('')
  const [wooSecret, setWooSecret] = useState('')
  const [merchantId, setMerchantId] = useState<string | null>(null)
  const [connectedStore, setConnectedStore] = useState<string | null>(null)
  const [isNewMerchant, setIsNewMerchant] = useState(true)
  const [setupWarnings, setSetupWarnings] = useState<string[]>([])
  const [showLegacyToken, setShowLegacyToken] = useState(false)

  // A Shopify install sends the merchant back as /onboarding?shopify_install=<claim>. Kept in
  // localStorage so it survives the Google sign-in round trip (AuthCallback routes back here).
  const [installClaim, setInstallClaim] = useState<string | null>(() => {
    const fromUrl = params.get('shopify_install')
    try {
      if (fromUrl) localStorage.setItem('flowstock.pendingShopifyInstall', fromUrl)
      return fromUrl ?? localStorage.getItem('flowstock.pendingShopifyInstall')
    } catch {
      return fromUrl
    }
  })

  // Step 3: products & suppliers, one product line at a time (OnboardingProductsAndSuppliers).
  // 'lines': only the merchant's brands and models (every supplier set to "Deselect all",
  // including suppliers approved later). 'everything': whatever the connected suppliers carry.
  const [sellMode, setSellMode] = useState<SellMode>('lines')
  const [lines, setLines] = useState<ProductLine[]>([])
  const [providers, setProviders] = useState<ProviderRow[]>([])
  const [connected, setConnected] = useState<Set<string>>(new Set())
  const [pending, setPending] = useState<Set<string>>(new Set())

  // Steps 5-6
  const initialMarkup = Number(params.get('markup'))
  const [pricing, setPricing] = useState<PricingChoice>(
    Number.isFinite(initialMarkup) && initialMarkup > 0 ? { ...DEFAULT_PRICING, markupPercent: initialMarkup } : DEFAULT_PRICING,
  )
  const [hasExistingPricing, setHasExistingPricing] = useState(false)
  const [replacePricing, setReplacePricing] = useState(true)
  const [importChoice, setImportChoice] = useState<ImportChoice>(DEFAULT_IMPORT)

  useEffect(() => {
    if (!contactEmail && user?.email) setContactEmail(user.email)
  }, [user, contactEmail])

  // Resume an unfinished setup. A store created here stays paused (sync_enabled=false) until Go live,
  // so a paused store means setup was interrupted: reloading /onboarding or "Finish setup" on the
  // dashboard must continue where it stopped, not restart at step 1, where a used install
  // link would leave the merchant stuck. A live store (sync_enabled absent or true) never resumes.
  const [resumeChecked, setResumeChecked] = useState(false)
  useEffect(() => {
    if (resumeChecked || authLoading || !user || installClaim || merchantId || !currentShopId) return
    setResumeChecked(true)
    supabase
      .from('merchant_config')
      .select('platform, shop_domain, config_object, business_info')
      .eq('merchant_id', currentShopId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data || (data.config_object as any)?.sync_enabled !== false) return
        const ship = (data.business_info as any)?.store_shipping_address ?? {}
        const bill = (data.business_info as any)?.store_billing_address ?? {}
        setShopName((v) => v || (data.config_object as any)?.client_name || '')
        setCompany((v) => v || bill.company_name || '')
        setVatId((v) => v || bill.vat_id || '')
        setStreet((v) => v || ship.street || '')
        setCity((v) => v || ship.city || '')
        setZip((v) => v || ship.zip_code || '')
        setCountry((v) => v || ship.country || '')
        setPhone((v) => v || ship.phone_number || '')
        if (ship.email) setContactEmail(ship.email)
        setPlatform(data.platform === 'woocommerce' ? 'woocommerce' : 'shopify')
        setMerchantId(currentShopId)
        setConnectedStore(data.shop_domain ?? null)
        // Step 3 reopens on the suppliers of the lines already chosen, so a merchant returning
        // after a supplier approved sees it connected.
        if ((data.config_object as any)?.new_supplier_selection_mode === 'select_all') setSellMode('everything')
        setStep(3)
        toast.info('Picking up where you left off: your store is connected.')
      })
  }, [resumeChecked, authLoading, user, installClaim, merchantId, currentShopId, toast])

  const forgetInstallClaim = useCallback(() => {
    try { localStorage.removeItem('flowstock.pendingShopifyInstall') } catch { /* ignore */ }
    // Drop ?shopify_install= from the address bar too, or a refresh would capture it again.
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.has('shopify_install')) {
        url.searchParams.delete('shopify_install')
        window.history.replaceState(null, '', url.pathname + (url.search ? url.search : '') + url.hash)
      }
    } catch { /* ignore */ }
    setInstallClaim(null)
  }, [])

  // Reconnecting from Shopify (Apps -> FlowStock) runs the same install. If the store already belongs
  // to this user, reconnect it at once and go to the dashboard instead of restarting onboarding.
  const [peeked, setPeeked] = useState(false)
  useEffect(() => {
    if (peeked || authLoading || !user || !installClaim) return
    setPeeked(true)
    ;(async () => {
      try {
        const info = await invokeFn<{ valid: boolean; owns_store?: boolean; shop?: string; merchant_id?: string }>(
          'shopify-oauth/peek', { claim_id: installClaim },
        )
        if (!info.valid) {
          // Already used (e.g. the page was refreshed after connecting) or expired: forget it, so an
          // unfinished setup resumes instead of offering to connect a store that is connected.
          forgetInstallClaim()
          return
        }
        if (!info.owns_store) return
        setLoading(true)
        await invokeFn('shopify-oauth/claim', { claim_id: installClaim })
        forgetInstallClaim()
        await refreshShops()
        if (info.merchant_id) setCurrentShopId(info.merchant_id)
        toast.success(`${info.shop ?? 'Your store'} is reconnected.`)
        window.location.href = '/dashboard'
      } catch (err: any) {
        toast.error(err.message || 'Could not reconnect your store.')
      } finally {
        setLoading(false)
      }
    })()
  }, [peeked, authLoading, user, installClaim, refreshShops, setCurrentShopId, toast, forgetInstallClaim])

  // ---- step 1 ---------------------------------------------------------------------------
  const businessComplete = planChosen && [shopName, contactEmail, company, street, city, zip, country, phone].every((v) => v.trim())

  const businessInfo = () => ({
    store_shipping_address: {
      name: company || shopName, street, city, zip_code: zip, country, phone_number: phone, email: contactEmail,
    },
    // Billing is the same address; it can be changed later under Settings.
    store_billing_address: { company_name: company, vat_id: vatId, street, city, zip_code: zip, country },
  })

  // ---- step 2 ---------------------------------------------------------------------------
  const connectStore = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      let result: any
      let store: string
      if (platform === 'shopify') {
        const domain = cleanShopifyDomain(shopifyDomain)
        if (!domain) throw new Error('Enter your store address, for example kickfield.myshopify.com')
        if (!accessToken.trim().startsWith('shpat_')) throw new Error('The access token starts with shpat_. Copy the whole token from your Shopify admin.')
        store = domain
        result = await invokeFn('deploy_merchant', {
          shopify_access_token: accessToken.trim(),
          shopify_shop_domain: domain,
          user_email: contactEmail.trim(),
          merchant_name: shopName.trim(),
          tier: 'standard',
          plan_id: planId ?? undefined,
          providers: [],
          auth_user_id: user?.id ?? null,
          run_initial_sync: false,
          skip_inventory_deploy: true,
          start_paused: true,
        })
      } else {
        const url = cleanWooUrl(wooUrl)
        if (!url) throw new Error('Enter your shop address, for example https://kickfield.nl')
        if (!wooKey.trim().startsWith('ck_') || !wooSecret.trim().startsWith('cs_')) {
          throw new Error('Paste both parts of the API key: the consumer key starts with ck_, the secret with cs_.')
        }
        store = url
        result = await invokeFn('deploy_merchant_woocommerce', {
          woocommerce_shop_url: url,
          woocommerce_consumer_key: wooKey.trim(),
          woocommerce_consumer_secret: wooSecret.trim(),
          user_email: contactEmail.trim(),
          merchant_name: shopName.trim(),
          auth_user_id: user?.id ?? null,
          plan_id: planId ?? undefined,
          skip_initial_sync: true,
          start_paused: true,
        })
      }

      await afterStoreCreated(result, store)
    } catch (err: any) {
      toast.error(err.message || 'Could not connect your store.')
    } finally {
      setLoading(false)
    }
  }

  const afterStoreCreated = async (result: any, store: string) => {
    const id: string | undefined = result?.merchant_id
    if (!id) throw new Error('Store setup did not return a merchant. Please try again.')

    const { error: bizErr } = await supabase.rpc('upsert_merchant_business_info', {
      p_merchant_id: id,
      p_business_info: businessInfo(),
    })
    if (bizErr) toast.error(`Your store is connected, but the business details were not saved: ${bizErr.message}`)

    const warnings = (result?.setup_warnings ?? []) as string[]
    setSetupWarnings(warnings)
    warnings.forEach((w) => toast.error(w))

    setMerchantId(id)
    setConnectedStore(store)
    setIsNewMerchant(result?.is_new_merchant !== false)
    await refreshShops()
    setCurrentShopId(String(id))
    setStep(3)
  }

  // Store installed through the FlowStock Shopify app: the server holds the token and creates the
  // merchant (shopify-oauth/claim -> deploy_merchant); the browser never sees the token.
  const claimShopifyInstall = async () => {
    if (!installClaim) return
    setLoading(true)
    try {
      const result = await invokeFn('shopify-oauth/claim', {
        claim_id: installClaim,
        merchant_name: shopName.trim(),
        contact_email: contactEmail.trim(),
        plan_id: planId ?? undefined,
      })
      forgetInstallClaim()
      await afterStoreCreated(result, result?.shop ?? 'your Shopify store')
    } catch (err: any) {
      toast.error(err.message || 'Could not connect your store.')
    } finally {
      setLoading(false)
    }
  }

  // ---- step 3 ---------------------------------------------------------------------------
  const loadSuppliers = useCallback(async () => {
    if (!merchantId) return
    const [{ data: provs }, { data: maps }, { data: reqs }] = await Promise.all([
      supabase.from('providers').select('id, name, provider_type').eq('is_active', true).order('name'),
      supabase.from('merchant_provider_map').select('provider_id').eq('merchant_id', merchantId),
      supabase.from('connection_requests').select('provider_id').eq('merchant_id', merchantId).eq('status', 'pending'),
    ])
    setProviders((provs as ProviderRow[]) ?? [])
    setConnected(new Set((maps ?? []).map((m: any) => m.provider_id)))
    setPending(new Set((reqs ?? []).map((r: any) => r.provider_id)))
  }, [merchantId])

  useEffect(() => { if (step === 3) loadSuppliers() }, [step, loadSuppliers])

  // ---- step 4 ---------------------------------------------------------------------------
  // ---- step 5 ---------------------------------------------------------------------------
  useEffect(() => {
    if (step !== 4 || !merchantId) return
    supabase.from('merchant_config').select('config_object').eq('merchant_id', merchantId).maybeSingle()
      .then(({ data }) => {
        const existing = !!(data?.config_object as any)?.pricing
        setHasExistingPricing(existing)
        // A store that already has pricing keeps it unless the merchant opts in to replacing it.
        if (existing) setReplacePricing(false)
      })
  }, [step, merchantId])

  // ---- step 6 ---------------------------------------------------------------------------
  const goLive = async () => {
    if (!merchantId) return
    setLoading(true)
    try {
      const { data: current, error: readErr } = await supabase
        .from('merchant_config').select('config_object').eq('merchant_id', merchantId).maybeSingle()
      if (readErr) throw new Error(`Could not read your store settings: ${readErr.message}`)

      const next = buildGoLiveConfig((current?.config_object as Record<string, any>) ?? {}, {
        pricing: clampPricing(pricing),
        importChoice,
        setPricing: replacePricing,
        clientName: shopName.trim(),
      })
      // Row security filters an UPDATE silently (no error, no rows), so check a row was written:
      // otherwise "You're live" would show while nothing changed.
      const { data: cfgRows, error: cfgErr } = await supabase
        .from('merchant_config').update({ config_object: next }).eq('merchant_id', merchantId).select('merchant_id')
      if (cfgErr) throw new Error(`Could not save your settings: ${cfgErr.message}`)
      if (!cfgRows?.length) throw new Error('Your account is not allowed to change this store\'s settings. Ask the store owner to give you editor access.')

      // Lines saved before any supplier approved matched nothing; match them now rather than on
      // the hourly refresh, so the first sync already sells them.
      if (lines.length > 0) {
        await invokeFn('merchant-product-lines', { shop_id: merchantId, action: 'refresh' })
          .catch(() => toast.info('Your product lines are matched within the hour.'))
      }

      // Real-time stock updates are gated on is_active; the full sync on sync_enabled (set above).
      // merchants has no client UPDATE policy (a direct update is silently dropped), so use the same
      // membership-checked RPC as the dashboard's sync switch.
      const { error: activeErr } = await supabase.rpc('toggle_merchant_sync', { p_merchant_id: merchantId, p_is_active: true })
      if (activeErr) throw new Error(`Settings saved, but real-time updates could not be switched on: ${activeErr.message}`)

      setStep(DONE)
    } catch (err: any) {
      toast.error(err.message || 'Could not go live.')
    } finally {
      setLoading(false)
    }
  }

  const example = computePrice(100, clampPricing(pricing))
  const selectedSuppliers = providers.filter((p) => connected.has(p.id) || pending.has(p.id))
  const wide = step === 3

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-12">
      <motion.div className={`w-full ${wide ? 'max-w-4xl' : 'max-w-2xl'}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        {step <= STEPS.length && (
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs text-white/40 mb-2">
              <span>Step {step} of {STEPS.length} · <span className="text-white/70">{STEPS[step - 1].title}</span></span>
              <span>{STEPS[step - 1].hint}</span>
            </div>
            <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
              <div className="h-full bg-orange-500 transition-all duration-300" style={{ width: `${(step / STEPS.length) * 100}%` }} />
            </div>
          </div>
        )}

        <div className="glass-card-strong rounded-2xl p-6 sm:p-8 relative overflow-hidden">
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at top, rgba(249,115,22,0.08) 0%, transparent 60%)', pointerEvents: 'none' }} />
          <div className="relative">
            {!authLoading && !user ? (
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-white text-xl">Sign in to set up your store</h3>
                <p className="text-white/50 text-sm">
                  {installClaim ? 'FlowStock is installed in your Shopify store. Sign in to connect it to your account.' : 'Your store will be linked to the account you sign in with.'}
                </p>
                <button type="button" className={primaryBtn} onClick={() => signInWithGoogle('merchant').catch((e) => toast.error(e.message))}>
                  Sign in with Google <ArrowRightIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
            <AnimatePresence mode="wait">
              {/* 1 · Business */}
              {step === 1 && (
                <motion.form key="s1" onSubmit={(e) => { e.preventDefault(); if (businessComplete) setStep(2) }}
                  initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                  <h3 className="font-heading font-bold text-white text-xl mb-1">Welcome to FLOWSTOCK</h3>
                  <p className="text-white/40 text-sm mb-6">Setup takes about 15 minutes. Suppliers use these details to deliver your orders.</p>
                  <OnboardingPlanChoice plans={plans} value={planId} onChange={setPlanId} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className={field}><label className={label} htmlFor="ob-name">Store name *</label>
                      <input id="ob-name" className="glass-input" value={shopName} onChange={(e) => setShopName(e.target.value)} required /></div>
                    <div className={field}><label className={label} htmlFor="ob-email">Contact email *</label>
                      <input id="ob-email" type="email" className="glass-input" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required /></div>
                    <div className={field}><label className={label} htmlFor="ob-co">Company name *</label>
                      <input id="ob-co" className="glass-input" value={company} onChange={(e) => setCompany(e.target.value)} required /></div>
                    <div className={field}><label className={label} htmlFor="ob-vat">VAT number</label>
                      <input id="ob-vat" className="glass-input" value={vatId} onChange={(e) => setVatId(e.target.value)} placeholder="NL123456789B01" /></div>
                    <div className={`${field} sm:col-span-2`}><label className={label} htmlFor="ob-street">Address for supplier deliveries *</label>
                      <input id="ob-street" className="glass-input" value={street} onChange={(e) => setStreet(e.target.value)} placeholder="Street and number" required /></div>
                    <div className={field}><label className={label} htmlFor="ob-city">City *</label>
                      <input id="ob-city" className="glass-input" value={city} onChange={(e) => setCity(e.target.value)} required /></div>
                    <div className={field}><label className={label} htmlFor="ob-zip">Postal code *</label>
                      <input id="ob-zip" className="glass-input" value={zip} onChange={(e) => setZip(e.target.value)} required /></div>
                    <div className={field}><label className={label} htmlFor="ob-country">Country code *</label>
                      <input id="ob-country" className="glass-input" value={country} maxLength={2} placeholder="NL" onChange={(e) => setCountry(e.target.value.toUpperCase())} required /></div>
                    <div className={field}><label className={label} htmlFor="ob-phone">Phone *</label>
                      <input id="ob-phone" type="tel" className="glass-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+31612345678" required /></div>
                  </div>
                  <p className="text-white/25 text-xs mt-3">Billing uses the same address. You can change it later under Settings.</p>
                  <div className="flex justify-end mt-6">
                    <button type="submit" className={primaryBtn} disabled={!businessComplete}>Continue <ArrowRightIcon className="w-4 h-4" /></button>
                  </div>
                </motion.form>
              )}

              {/* 2 · Store */}
              {step === 2 && (
                <motion.form key="s2" onSubmit={connectStore}
                  initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                  <h3 className="font-heading font-bold text-white text-xl mb-1">Connect your store</h3>
                  <p className="text-white/40 text-sm mb-5">FlowStock only ever writes to its own stock location in your store. Stock you hold elsewhere is never touched.</p>

                  <div className="inline-flex rounded-full border border-white/10 p-1 mb-5" role="group" aria-label="Platform">
                    {(['shopify', 'woocommerce'] as Platform[]).map((p) => (
                      <button key={p} type="button" aria-pressed={platform === p} onClick={() => setPlatform(p)} disabled={!!merchantId || !!installClaim}
                        className={`px-4 py-1.5 rounded-full text-sm transition-colors ${platform === p ? 'bg-orange-500 text-black font-semibold' : 'text-white/50 hover:text-white'}`}>
                        {p === 'shopify' ? 'Shopify' : 'WooCommerce'}
                      </button>
                    ))}
                  </div>

                  {merchantId ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm text-white/70"><CheckCircleIcon className="w-5 h-5 text-orange-400" /> Connected to <span className="font-mono text-white">{connectedStore}</span></div>
                      {setupWarnings.length > 0 && <Warnings items={setupWarnings} />}
                      <div className="flex justify-between">
                        <button type="button" className={ghostBtn} onClick={() => setStep(1)}>Back</button>
                        <button type="button" className={primaryBtn} onClick={() => setStep(3)}>Continue <ArrowRightIcon className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ) : platform === 'shopify' ? (
                    <div className="space-y-4">
                      {installClaim ? (
                        <>
                          <div className="p-4 rounded-lg border border-orange-500/30 bg-orange-500/5 text-sm text-white/80">
                            FlowStock is installed in your Shopify store. Connect it to finish setting up.
                          </div>
                          <div className="flex justify-between pt-2">
                            <button type="button" className={ghostBtn} onClick={() => setStep(1)}>Back</button>
                            <button type="button" className={primaryBtn} disabled={loading} onClick={claimShopifyInstall}>
                              {loading ? 'Connecting… (up to a minute)' : <>Connect store <ArrowRightIcon className="w-4 h-4" /></>}
                            </button>
                          </div>
                        </>
                      ) : !showLegacyToken ? (
                        <>
                          <ol className="list-decimal pl-5 space-y-1 text-sm text-white/60">
                            <li>Open the <b className="text-white/80">FlowStock install link</b> you received, while signed in to your Shopify admin.</li>
                            <li>Shopify shows the permissions FlowStock needs. Click <b className="text-white/80">Install</b>.</li>
                            <li>You come straight back here to finish connecting.</li>
                          </ol>
                          <p className="text-xs text-white/35">No install link yet? Ask the person who invited you to FlowStock. Each store gets its own link for now.</p>
                          <button type="button" className="text-xs text-white/40 hover:text-white/70 underline" onClick={() => setShowLegacyToken(true)}>
                            My store has an Admin API token from an app created before 2026
                          </button>
                          <div className="flex justify-start pt-2">
                            <button type="button" className={ghostBtn} onClick={() => setStep(1)}>Back</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-white/40">Only for stores whose custom app was created before 1 January 2026. Shopify no longer lets stores create new ones.</p>
                          <ol className="list-decimal pl-5 space-y-1 text-sm text-white/60">
                        <li>In your Shopify admin open <b className="text-white/80">Settings → Apps and sales channels → Develop apps</b> and create an app named FlowStock.</li>
                        <li>Under <b className="text-white/80">Configuration → Admin API integration</b>, tick these permissions:
                          <span className="flex flex-wrap gap-1.5 mt-1.5">{SHOPIFY_SCOPES.map((s) => <code key={s} className="text-xs bg-white/[0.06] text-white/70 px-1.5 py-0.5 rounded">{s}</code>)}</span></li>
                        <li>Install the app, reveal the <b className="text-white/80">Admin API access token</b> once and paste it below.</li>
                      </ol>
                      <div className={field}><label className={label} htmlFor="ob-dom">Store address</label>
                        <input id="ob-dom" className="glass-input font-mono" value={shopifyDomain} onChange={(e) => setShopifyDomain(e.target.value)} placeholder="your-store.myshopify.com" required /></div>
                      <div className={field}><label className={label} htmlFor="ob-tok">Admin API access token</label>
                        <input id="ob-tok" type="password" autoComplete="off" className="glass-input font-mono" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder="shpat_…" required /></div>
                          <StoreFooter loading={loading} onBack={() => setShowLegacyToken(false)} />
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <ol className="list-decimal pl-5 space-y-1 text-sm text-white/60">
                        <li>In WordPress open <b className="text-white/80">WooCommerce → Settings → Advanced → REST API</b> and click <b className="text-white/80">Add key</b>.</li>
                        <li>Name it FlowStock and set permissions to <b className="text-white/80">Read/Write</b>.</li>
                        <li>Generate the key and paste both parts below. The secret is shown only once.</li>
                      </ol>
                      <div className={field}><label className={label} htmlFor="ob-wurl">Shop address</label>
                        <input id="ob-wurl" className="glass-input font-mono" value={wooUrl} onChange={(e) => setWooUrl(e.target.value)} placeholder="https://your-shop.com" required /></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className={field}><label className={label} htmlFor="ob-ck">Consumer key</label>
                          <input id="ob-ck" autoComplete="off" className="glass-input font-mono" value={wooKey} onChange={(e) => setWooKey(e.target.value)} placeholder="ck_…" required /></div>
                        <div className={field}><label className={label} htmlFor="ob-cs">Consumer secret</label>
                          <input id="ob-cs" type="password" autoComplete="off" className="glass-input font-mono" value={wooSecret} onChange={(e) => setWooSecret(e.target.value)} placeholder="cs_…" required /></div>
                      </div>
                      <StoreFooter loading={loading} onBack={() => setStep(1)} />
                    </div>
                  )}
                </motion.form>
              )}

              {/* 3 · Products & suppliers */}
              {step === 3 && merchantId && user?.id && (
                <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                  <OnboardingProductsAndSuppliers
                    merchantId={merchantId}
                    userId={user.id}
                    isNewMerchant={isNewMerchant}
                    providers={providers}
                    connected={connected}
                    pending={pending}
                    reloadSuppliers={loadSuppliers}
                    sellMode={sellMode}
                    setSellMode={setSellMode}
                    onLinesChange={setLines}
                    onBack={() => setStep(2)}
                    onDone={() => setStep(4)}
                  />
                  {!isNewMerchant && (
                    <p className="text-xs text-white/40 mt-3">This store was set up before: suppliers already connected keep their current setting. Change it under Product lines on the dashboard.</p>
                  )}
                </motion.div>
              )}

              {/* 4 · Pricing */}
              {step === 4 && (
                <motion.div key="s5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                  <h3 className="font-heading font-bold text-white text-xl mb-1">How should prices be set?</h3>
                  <p className="text-white/40 text-sm mb-5">Your price = supplier price × markup × VAT + delivery fee, then rounded.</p>
                  {hasExistingPricing && (
                    <label className="flex items-start gap-3 mb-5 p-3 rounded-lg border border-white/10 text-sm text-white/70 cursor-pointer">
                      <input type="checkbox" className="accent-orange-500 mt-1" checked={replacePricing} onChange={(e) => setReplacePricing(e.target.checked)} />
                      <span>Your store already has pricing set up. Replace it with the settings below? Leave unticked to keep your current pricing.</span>
                    </label>
                  )}
                  <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 ${hasExistingPricing && !replacePricing ? 'opacity-40 pointer-events-none' : ''}`}>
                    <NumField id="pr-m" label="Markup %" value={pricing.markupPercent} onChange={(v) => setPricing({ ...pricing, markupPercent: v })} />
                    <NumField id="pr-v" label="VAT %" value={pricing.vatPercent} onChange={(v) => setPricing({ ...pricing, vatPercent: v })} />
                    <NumField id="pr-f" label="Delivery fee €" value={pricing.deliveryFee} onChange={(v) => setPricing({ ...pricing, deliveryFee: v })} />
                    <div className={field}><label className={label} htmlFor="pr-r">Rounding</label>
                      <select id="pr-r" className="glass-input" value={pricing.rounding} onChange={(e) => setPricing({ ...pricing, rounding: e.target.value as RoundingMode })}>
                        {ROUNDING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-5">
                    {[80, 120, 200].map((c) => (
                      <div key={c} className="glass-card rounded-xl p-3">
                        <p className="text-xs text-white/40">Supplier €{c}</p>
                        <p className="font-mono text-lg text-orange-300">€{computePrice(c, clampPricing(pricing)).price.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-white/35 font-mono mt-3 overflow-x-auto whitespace-nowrap">
                    €100 × {(1 + pricing.markupPercent / 100).toFixed(2)} × {(1 + pricing.vatPercent / 100).toFixed(2)}{pricing.deliveryFee ? ` + ${pricing.deliveryFee}` : ''} = €{example.beforeRounding.toFixed(2)} → €{example.price.toFixed(2)}
                  </p>
                  <p className="text-xs text-white/25 mt-2">Need different markups by price range or per supplier? Set that up later on the Processors page.</p>
                  <div className="flex justify-between mt-6">
                    <button type="button" className={ghostBtn} onClick={() => setStep(3)}>Back</button>
                    <button type="button" className={primaryBtn} onClick={() => setStep(5)}>Continue <ArrowRightIcon className="w-4 h-4" /></button>
                  </div>
                </motion.div>
              )}

              {/* 5 · Import */}
              {step === 5 && (
                <motion.div key="s6" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                  <h3 className="font-heading font-bold text-white text-xl mb-1">How should products arrive?</h3>
                  <p className="text-white/40 text-sm mb-5">Stock is always kept up to date. Choose what else FlowStock updates.</p>
                  <div className="divide-y divide-white/[0.06]">
                    {([
                      ['newProductsAsDraft', 'Create new products as drafts', 'Review them before they go on sale'],
                      ['updateTitles', 'Keep titles and brands updated', 'Turn off if you write your own titles'],
                      ['updateImages', 'Keep images updated', 'Turn off if you use your own photos'],
                      ['updatePrices', 'Keep prices updated', 'Applies your pricing to every supplier price change'],
                    ] as [keyof ImportChoice, string, string][]).map(([k, title, hint]) => (
                      <div key={k} className="flex items-center justify-between gap-4 py-3">
                        <div><p className="text-sm text-white">{title}</p><p className="text-xs text-white/35">{hint}</p></div>
                        <button type="button" role="switch" aria-checked={importChoice[k]} aria-label={title}
                          onClick={() => setImportChoice({ ...importChoice, [k]: !importChoice[k] })}
                          className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${importChoice[k] ? 'bg-orange-500' : 'bg-white/10'}`}>
                          <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${importChoice[k] ? 'translate-x-4' : ''}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-6">
                    <button type="button" className={ghostBtn} onClick={() => setStep(4)}>Back</button>
                    <button type="button" className={primaryBtn} onClick={() => setStep(6)}>Continue <ArrowRightIcon className="w-4 h-4" /></button>
                  </div>
                </motion.div>
              )}

              {/* 6 · Review */}
              {step === 6 && (
                <motion.div key="s7" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                  <h3 className="font-heading font-bold text-white text-xl mb-1">Ready to go live</h3>
                  <p className="text-white/40 text-sm mb-5">Check your choices. Everything can be changed later from the dashboard.</p>
                  {merchantId && (
                    <SetupFeeCard merchantId={merchantId} email={contactEmail.trim() || undefined} plans={plans} onAwaitingChange={setAwaitingSetup} />
                  )}
                  <dl className="divide-y divide-white/[0.06] text-sm">
                    <Row k="Store">{shopName} · {platform === 'shopify' ? 'Shopify' : 'WooCommerce'} · <span className="font-mono">{connectedStore}</span></Row>
                    <Row k="What to sell">{sellMode === 'everything' ? 'Everything your suppliers carry' : lines.length ? lines.map((l) => l.name).join(', ') : 'Nothing picked yet'}</Row>
                    <Row k="Suppliers">{selectedSuppliers.length ? selectedSuppliers.map((p) => `${p.name}${pending.has(p.id) ? ' (waiting)' : ''}`).join(', ') : 'None yet'}</Row>
                    <Row k="Pricing">{hasExistingPricing && !replacePricing ? 'Keep your current pricing' : `${pricing.markupPercent}% markup, ${pricing.vatPercent}% VAT${pricing.deliveryFee ? `, €${pricing.deliveryFee} delivery` : ''}, ${ROUNDING_OPTIONS.find((o) => o.value === pricing.rounding)?.label.toLowerCase()} · €100 → €${example.price.toFixed(2)}`}</Row>
                    <Row k="New products">{importChoice.newProductsAsDraft ? 'Created as drafts' : 'Live immediately'}</Row>
                  </dl>
                  {setupWarnings.length > 0 && <div className="mt-4"><Warnings items={setupWarnings} /></div>}
                  {pending.size > 0 && connected.size === 0 && (
                    <p className="text-xs text-yellow-300/70 mt-4">Your suppliers haven't approved yet. Going live now is fine: products start arriving as soon as one does.</p>
                  )}
                  <div className="flex justify-between mt-6">
                    <button type="button" className={ghostBtn} onClick={() => setStep(5)}>Back</button>
                    <button type="button" className={primaryBtn} disabled={loading || awaitingSetup} onClick={goLive}>{loading ? 'Starting…' : 'Go live'}</button>
                  </div>
                  {!isNewMerchant && <p className="text-xs text-white/30 mt-3">This store was already set up before. Going live keeps its existing rules and only applies what you changed here.</p>}
                </motion.div>
              )}

              {/* Done */}
              {step === DONE && (
                <motion.div key="done" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
                  <div className="w-14 h-14 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center mb-4">
                    <CheckCircleIcon className="w-8 h-8 text-orange-400" />
                  </div>
                  <h3 className="font-heading font-bold text-white text-2xl mb-1">You're live</h3>
                  <p className="text-white/40 mb-6">The first sync starts within a few minutes and takes 10–30 minutes, depending on how much you sell.</p>
                  {setupWarnings.length > 0 && <div className="mb-6"><Warnings items={setupWarnings} /></div>}
                  <div className="space-y-4 text-sm mb-7">
                    <Know title="How sync works">FlowStock keeps one stock location in your store, named FLOWSTOCK, and only ever writes there. Stock you hold elsewhere is never touched.</Know>
                    <Know title="Sync timing">A single stock change usually reaches your store in about 3½ minutes, and almost always within 15. Your whole catalog is re-checked every 15–60 minutes. A product that looks out of date for a few minutes is normal, and the dashboard shows when your store last synced.</Know>
                    <Know title="How orders are routed">Each order line goes to the cheapest supplier with the size in stock. The order is tagged FLOWSTOCK:&lt;supplier&gt; so you can see who fulfils it.</Know>
                    <Know title="Need help?">Contact the person who invited you to FlowStock.</Know>
                  </div>
                  <button onClick={() => { window.location.href = '/dashboard' }} className={primaryBtn}>Go to dashboard <ArrowRightIcon className="w-4 h-4" /></button>
                </motion.div>
              )}
            </AnimatePresence>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  )
}

const StoreFooter: React.FC<{ loading: boolean; onBack: () => void }> = ({ loading, onBack }) => (
  <div className="flex justify-between pt-2">
    <button type="button" className={ghostBtn} onClick={onBack}>Back</button>
    <button type="submit" className={primaryBtn} disabled={loading}>{loading ? 'Connecting… (up to a minute)' : <>Connect store <ArrowRightIcon className="w-4 h-4" /></>}</button>
  </div>
)

const Warnings: React.FC<{ items: string[] }> = ({ items }) => (
  <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 space-y-2">
    <p className="text-red-300 font-medium text-sm flex items-center gap-2"><ExclamationTriangleIcon className="w-4 h-4" /> Your store needs attention before sync can run:</p>
    {items.map((w) => <p key={w} className="text-red-200/80 text-sm">{w}</p>)}
  </div>
)

// Keeps what the merchant typed, so clearing the field shows it empty (the value is 0 meanwhile).
// Rendering the number directly turned "clear, type 30" into "030".
const NumField: React.FC<{ id: string; label: string; value: number; onChange: (v: number) => void }> = ({ id, label: text, value, onChange }) => {
  const [draft, setDraft] = useState(Number.isFinite(value) ? String(value) : '')
  useEffect(() => {
    // Follow outside changes (e.g. an invite link's markup) without fighting the typing.
    setDraft((d) => ((d === '' ? 0 : Number(d)) === value ? d : String(value)))
  }, [value])
  return (
    <div className={field}>
      <label className={label} htmlFor={id}>{text}</label>
      <input id={id} type="number" min={0} step="any" className="glass-input font-mono" value={draft}
        onChange={(e) => { setDraft(e.target.value); onChange(e.target.value === '' ? 0 : Number(e.target.value)) }} />
    </div>
  )
}

const Row: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] gap-1 sm:gap-4 py-2.5">
    <dt className="text-white/40">{k}</dt>
    <dd className="text-white/80 min-w-0 break-words">{children}</dd>
  </div>
)

const Know: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className="text-white font-semibold">{title}</p>
    <p className="text-white/50">{children}</p>
  </div>
)

export default ShopOnboarding
